import { useState } from "react";
import type { MouseEvent } from "react";
import {
  Badge, Box, Divider, IconButton, List, ListItem, ListItemButton, Menu, MenuItem, Popover, Stack, Tooltip, Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DoneIcon from "@mui/icons-material/Done";
import SnoozeIcon from "@mui/icons-material/Snooze";
import NotificationsIcon from "@mui/icons-material/Notifications";
import NotificationsNoneIcon from "@mui/icons-material/NotificationsNone";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import {
  BILL_REMINDERS_KEY, dismissBillReminder, fetchBillReminders, formatRemindAt, inHours, nextMondayAt, snoozeBillReminder, tomorrowAt,
  type BillReminder,
} from "@/api/billReminders";

// Header bell: your bill reminders. Due ones count on the badge and sit on top;
// upcoming ones follow. Polls, since the app doesn't refetch on window focus.
export function NotificationsTray() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const [snoozeFor, setSnoozeFor] = useState<{ anchor: HTMLElement; id: number } | null>(null);

  const list = useQuery({ queryKey: BILL_REMINDERS_KEY, queryFn: fetchBillReminders, refetchInterval: 60_000 });
  const onDone = () => qc.invalidateQueries({ queryKey: BILL_REMINDERS_KEY });
  const snooze = useMutation({ mutationFn: ({ id, at }: { id: number; at: Date }) => snoozeBillReminder(id, at), onSuccess: onDone });
  const dismiss = useMutation({ mutationFn: (id: number) => dismissBillReminder(id), onSuccess: onDone });

  const reminders = list.data?.reminders ?? [];
  const due = reminders.filter((r) => r.due);
  const upcoming = reminders.filter((r) => !r.due);
  const dueCount = list.data?.due_count ?? 0;

  const open = (r: BillReminder) => { setAnchor(null); navigate(r.path); };

  // Title on its own line; time and status underneath; the note last. Actions
  // are compact icons on the right so long titles keep their width.
  const row = (r: BillReminder) => (
    <ListItem key={r.id} disablePadding divider
      sx={{ alignItems: "stretch", borderLeft: 3, borderLeftColor: r.due ? "secondary.main" : "transparent" }}>
      <ListItemButton onClick={() => open(r)} sx={{ flex: 1, minWidth: 0, py: 1, alignItems: "flex-start" }}>
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="body2" fontWeight={r.due ? 600 : 500} noWrap title={r.bill.title}>{r.bill.title}</Typography>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.25 }}>
            <Typography variant="caption" color={r.due ? "secondary.dark" : "text.secondary"} noWrap>
              {formatRemindAt(r.remind_at)}
            </Typography>
            <Typography variant="caption" color="text.disabled">·</Typography>
            <Typography variant="caption" color="text.secondary" noWrap>{r.bill.status_label}</Typography>
          </Stack>
          {r.note && (
            <Typography variant="caption" color="text.secondary"
              sx={{ display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden", mt: 0.25, fontStyle: "italic" }}>
              {r.note}
            </Typography>
          )}
        </Box>
      </ListItemButton>
      <Stack direction="row" alignItems="center" sx={{ pr: 1, flexShrink: 0 }}>
        {r.due && (
          <Tooltip title="Snooze">
            <IconButton size="small" aria-label="Snooze" onClick={(e) => setSnoozeFor({ anchor: e.currentTarget, id: r.id })}>
              <SnoozeIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
        <Tooltip title={r.due ? "Dismiss" : "Cancel reminder"}>
          <IconButton size="small" aria-label={r.due ? "Dismiss" : "Cancel reminder"} onClick={() => dismiss.mutate(r.id)}>
            {r.due ? <DoneIcon fontSize="small" /> : <CloseIcon fontSize="small" />}
          </IconButton>
        </Tooltip>
      </Stack>
    </ListItem>
  );

  const snoozeTo = (at: Date) => {
    if (snoozeFor) snooze.mutate({ id: snoozeFor.id, at });
    setSnoozeFor(null);
  };

  const section = (title: string, items: BillReminder[]) =>
    items.length > 0 && (
      <>
        <Typography variant="overline" color="text.secondary" sx={{ px: 2, pt: 1, display: "block", lineHeight: 2 }}>{title}</Typography>
        <List dense disablePadding>{items.map(row)}</List>
      </>
    );

  return (
    <>
      <Tooltip title="Bill reminders">
        <IconButton color="inherit" aria-label={`Bill reminders, ${dueCount} due`}
          onClick={(e: MouseEvent<HTMLElement>) => setAnchor(e.currentTarget)} sx={{ flexShrink: 0 }}>
          <Badge badgeContent={dueCount} color="secondary">
            {dueCount > 0 ? <NotificationsIcon /> : <NotificationsNoneIcon />}
          </Badge>
        </IconButton>
      </Tooltip>
      <Popover open={Boolean(anchor)} anchorEl={anchor} onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }} transformOrigin={{ vertical: "top", horizontal: "right" }}
        slotProps={{ paper: { sx: { width: 420, maxWidth: "calc(100vw - 32px)", maxHeight: 520 } } }}>
        <Box sx={{ px: 2, py: 1.5, display: "flex", alignItems: "baseline", justifyContent: "space-between" }}>
          <Typography variant="subtitle1" fontWeight={600}>Bill reminders</Typography>
          {dueCount > 0 && <Typography variant="caption" color="text.secondary">{dueCount} due</Typography>}
        </Box>
        <Divider />
        {reminders.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
            No reminders. Use “Remind me” on a bill to come back to it later.
          </Typography>
        ) : (
          <>
            {section("Due", due)}
            {section("Upcoming", upcoming)}
          </>
        )}
      </Popover>
      <Menu anchorEl={snoozeFor?.anchor} open={Boolean(snoozeFor)} onClose={() => setSnoozeFor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }} transformOrigin={{ vertical: "top", horizontal: "right" }}>
        <MenuItem dense onClick={() => snoozeTo(inHours(1))}>For 1 hour</MenuItem>
        <MenuItem dense onClick={() => snoozeTo(tomorrowAt(8))}>Until tomorrow 8:00 AM</MenuItem>
        <MenuItem dense onClick={() => snoozeTo(nextMondayAt(8))}>Until Monday 8:00 AM</MenuItem>
      </Menu>
    </>
  );
}
